import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { ShareNetwork, CheckCircle } from '@phosphor-icons/react';
import { Icon } from '../../design-system/icons/Icon';
import { Link } from '../Link';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { trackJobClick } from '../../utils/analytics/analytics';
import { MatchDonutWithLoginPrompt } from '../MatchDonut';
import { useHitShared } from './hooks/useHitShared';
import { ApplicationStateMenu } from '../application-state';
import { getSinglePrioritizedLocation } from '../location/utils/locationUtils';
import { formatSrpSalaryParts } from '../../utils/data/format-job-card-labels';
import { resolveSalaryInferenceExplanationKind } from '../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { normalizeToStringArray } from '../../utils/data/normalize-to-string-array';
import type { DisplayJob } from '../../types';
import { useFormatMessage } from '../../i18n-review/client/Msg';

export interface HitCompanyGroupRoleRowProps {
  hit: DisplayJob;
  sendEvent?: (eventType: string, hit: DisplayJob, eventName?: string, payload?: object) => void;
  /** Brand panel color — used as the donut ring override, same as the single-job card. */
  panelColor: string;
}

/**
 * One role row inside a multi-job company card (HitCompanyGroupCard). Owns its own useHitShared
 * instance so each role gets its own match donut/save/share/more-options — mirrors the top-row
 * actions cluster on HitCompactV2.tsx, just sized down to fit a list row instead of a full card.
 */
export const HitCompanyGroupRoleRow: React.FC<HitCompanyGroupRoleRowProps> = React.memo(({ hit, sendEvent, panelColor }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const shared = useHitShared({ hit, sendEvent });
  const {
    jobUrl,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    matchScore,
    matchScoreData,
    matchScoreMeta,
    isAuthenticated,
    onLoginClick,
    actions,
    menuOpenSource,
    setMenuOpenSource,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
  } = shared;

  const location = useMemo(() => getSinglePrioritizedLocation(hit, undefined), [hit]);
  const salaryParts = useMemo(
    () => formatSrpSalaryParts({ salaryRangeMin: hit.salaryRangeMin, salaryRangeMax: hit.salaryRangeMax, locale: intl.locale }),
    [hit.salaryRangeMin, hit.salaryRangeMax, intl.locale],
  );
  const hasSalary = salaryParts.length > 0;
  const isEstimate = Boolean(hit.salaryIsEstimate);
  const inferenceKind = resolveSalaryInferenceExplanationKind({
    salaryIsEstimate: hit.salaryIsEstimate,
    salaryRangeMin: hit.salaryRangeMin,
    salaryRangeMax: hit.salaryRangeMax,
    salaryDetails: hit.salaryDetails,
  });
  const isVerified = hasSalary && inferenceKind === null;
  const requiredLanguageTags = useMemo(() => normalizeToStringArray(hit.requiredLanguages), [hit.requiredLanguages]);

  // Individual fields (not one pre-joined string) so the container can flex-wrap them — a
  // "· last item" that runs out of room moves whole to the next line instead of the line being
  // cut with an ellipsis and that field disappearing entirely.
  const metaFields = useMemo(() => {
    const fields: { key: string; label: string; facet?: string; value?: string }[] = [];
    const locationDisplay = location.display ? fixMojibake(location.display) : '';
    if (locationDisplay && location.locationForUrl) fields.push({ key: 'location', label: locationDisplay, facet: 'location', value: location.locationForUrl });
    else if (locationDisplay) fields.push({ key: 'location', label: locationDisplay });
    if (hit.remoteness?.trim()) fields.push({ key: 'remote', label: fixMojibake(hit.remoteness), facet: 'remoteness', value: hit.remoteness });
    if (hit.jobType?.trim()) fields.push({ key: 'jobType', label: fixMojibake(hit.jobType), facet: 'jobType', value: hit.jobType });
    if (hit.normalisedRoleLevel?.trim()) fields.push({ key: 'roleLevel', label: fixMojibake(hit.normalisedRoleLevel), facet: 'normalisedRoleLevel', value: hit.normalisedRoleLevel });
    if (requiredLanguageTags.length > 0) fields.push({ key: 'language', label: requiredLanguageTags.map(fixMojibake).join(', ') });
    return fields;
  }, [hit.remoteness, hit.jobType, hit.normalisedRoleLevel, requiredLanguageTags, location.display, location.locationForUrl]);

  const title = fixMojibake(hit.normalisedTitle || hit.title || '');

  const salaryNotSpecifiedLabel = formatMessage({ id: 'hit.salaryNotSpecified.card', defaultMessage: 'Salary not specified', description: 'Shown when salary is not specified in job card' });
  const monthlyLabel = formatMessage({ id: 'hitV2.monthly', defaultMessage: 'Monthly', description: 'Salary period label on the redesigned job card panel' });
  const estMonthlyLabel = formatMessage({ id: 'hitV2.estMonthly', defaultMessage: 'Est. Monthly', description: 'Salary period label on the redesigned job card panel, estimated figure' });
  const viewJobLabel = formatMessage({ id: 'hitV2.viewJob', defaultMessage: 'View job', description: 'Primary CTA button on the redesigned job card' });

  const handleRoleClick = () => {
    if (sendEvent) {
      sendEvent('click', hit, 'Job Details Clicked');
      sendEvent('conversion', hit, 'Job Details Clicked');
    }
    trackJobClick({
      jobId: hit.objectID,
      jobTitle: hit.normalisedTitle || hit.title || '',
      companyId: hit.companyId || '',
      locale: intl.locale,
    });
  };

  return (
    <li className="hit-card-v2-group__role-row">
      <div className="hit-card-v2-group__role-main">
        <Link href={jobUrl} className="hit-card-v2-group__role-title" onClick={handleRoleClick}>
          {title}
        </Link>
        <div className="hit-card-v2-group__role-salary-row">
          {hasSalary ? (
            <span className={`hit-card-v2__salary-chip ${isVerified ? 'hit-card-v2__salary-chip--verified' : ''}`}>
              {isVerified ? <CheckCircle weight="fill" className="hit-card-v2__salary-chip-icon" /> : null}
              {salaryParts.map((amt, i) => (
                <span className="hit-card-v2__salary-chip-amt" key={i}>
                  {i > 0 ? `- ${amt}` : amt}
                </span>
              ))}
              <span className="hit-card-v2__salary-chip-unit">{isEstimate ? estMonthlyLabel : monthlyLabel}</span>
            </span>
          ) : (
            <span className="hit-card-v2__salary-not-specified">{salaryNotSpecifiedLabel}</span>
          )}
        </div>
        {metaFields.length > 0 ? (
          <div className="hit-card-v2-group__role-meta">
            {metaFields.map((field, i) => (
              <React.Fragment key={field.key}>
                {i > 0 ? <span aria-hidden className="hit-card-v2-group__role-meta-sep">·</span> : null}
                {field.facet && field.value ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState(field.facet, field.value))}
                    className="hit-card-v2-group__role-meta-item hit-card-v2-group__role-meta-item--link"
                    onClick={(e) => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState(field.facet as string, field.value as string));
                    }}
                  >
                    {field.label}
                  </Link>
                ) : (
                  <span className="hit-card-v2-group__role-meta-item">{field.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>

      {/* Actions + CTA share one bottom row (not sitting beside the title, like the single-job
       * card's top-row does) — title/salary/meta need the card's full width to stay legible,
       * especially at the narrow end of this card's fluid width. */}
      <div className="hit-card-v2-group__role-bottom">
        <div className="hit-card-v2-group__role-actions">
          <MatchDonutWithLoginPrompt
            score={matchScore}
            size={26}
            onLoginClick={onLoginClick}
            breakdown={matchScoreData ?? undefined}
            isAuthenticated={isAuthenticated}
            matchScoreMeta={matchScoreMeta}
            ringColorOverride={panelColor}
            labelColorOverride={{ light: '#16150F', dark: '#F3EFE4' }}
          />
          <button
            type="button"
            className={`hit-card-v2__icon-btn ${isSaved ? 'hit-card-v2__icon-btn--saved' : ''}`}
            aria-label={
              isSaved
                ? formatMessage({ id: 'hit.saved', defaultMessage: 'Saved', description: 'Button label when job is saved' })
                : formatMessage({ id: 'hit.save', defaultMessage: 'Save', description: 'Button label to save a job' })
            }
            onClick={actions.handleToggleSaved}
          >
            <Icon name="bookmark" variant={isSaved ? 'solid' : 'outline'} useMaskForColor className="hit-card-v2__icon" />
          </button>
          <button
            type="button"
            className="hit-card-v2__icon-btn"
            aria-label={formatMessage({ id: 'hit.share.detail', defaultMessage: 'Share', description: 'Button label to share a job on detail page' })}
            onClick={() => void actions.handleShare()}
          >
            <ShareNetwork className="hit-card-v2__icon h-3.5 w-3.5" />
          </button>
          <button
            ref={menuButtonRef}
            type="button"
            className="hit-card-v2__icon-btn"
            aria-label={formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options' })}
            onClick={() => setMenuOpenSource(menuOpenSource ? null : 'desktop')}
          >
            <Icon name="ellipsisVertical" useMaskForColor className="hit-card-v2__icon" />
          </button>
        </div>

        <Link href={jobUrl} className="hit-card-v2-group__role-cta" onClick={handleRoleClick}>
          {viewJobLabel}
        </Link>
      </div>

      {menuOpenSource && (
        <ApplicationStateMenu
          currentApplicationState={currentApplicationState}
          isApplied={isApplied}
          isHidden={isHidden}
          onSetApplicationState={actions.handleSetApplicationState}
          onToggleApplied={actions.handleToggleApplied}
          onToggleHide={actions.handleToggleHide}
          onClose={() => setMenuOpenSource(null)}
          className="w-56"
          triggerRef={menuButtonRef as unknown as React.RefObject<HTMLElement>}
          usePortal={true}
        />
      )}
    </li>
  );
});

HitCompanyGroupRoleRow.displayName = 'HitCompanyGroupRoleRow';
